<template>
  <section class="guided-analyses-should-you-call">
    <readonly-alert v-if="!authorized" />
    <saved-scenario-alert
      v-if="savedScenario"
      v-bind:title="savedScenario.title"
    />
    <header class="page-header">
      <h1>Guided Analysis: Should you call?</h1>
    </header>
    <h4>Step 1: What street are you deciding whether or not to call on?</h4>
    <street-selector
      v-bind:street="state.streetToCall"
      v-bind:authorized="authorized"
      v-on:setStreet="setStreetToCall($event)"
    />
    <h4>Step 2: How many opponents are you facing on that street?</h4>
    <num-opponents-selector
      v-bind:numOpponents="state.players.numPlayers"
      v-bind:authorized="authorized"
      v-on:addOpponent="addPlayer()"
      v-on:removeOpponent="removePlayer()"
    />
    <h4>Step 3: What are your hole cards?</h4>
    <hole-cards-selector
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:presetHoleCards="state.presetHoleCards"
      v-bind:popoverDirectionClass="'dropdown'"
      v-bind:disabled="!authorized"
      v-on:setPresetHoleCard="setPresetHoleCard($event)"
      v-on:removePresetHoleCard="removePresetHoleCard($event)"
    />
    <preflop-steps
      ref="preflopSteps"
      v-bind:players="state.players"
      v-bind:presetCommunityCards="state.presetCommunityCards"
      v-bind:streetToCall="state.streetToCall"
      v-bind:authorized="authorized"
    />
    <flop-steps
      v-if="state.presetHoleCards.bothSet"
      v-bind:state="state"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(1)"
      v-bind:authorized="authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event.card, $event.position)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
      v-on:setBoard="setBoard($event)"
      v-on:setPotOdds="setPotOdds($event)"
      v-on:setRealOddsLHS="setRealOddsLHS($event)"
      v-on:setBettingCriteria="setBettingCriteria()"
    />
    <turn-steps
      v-if="(state.streetToCall === 'turn' || state.streetToCall === 'river') && state.presetCommunityCards.flopSet && state.presetHoleCards.bothSet"
      v-bind:state="state"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(2)"
      v-bind:authorized="authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event.card, $event.position)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
      v-on:setPotOdds="setPotOdds($event)"
      v-on:setRealOddsLHS="setRealOddsLHS($event)"
      v-on:setBettingCriteria="setBettingCriteria()"
    />
    <river-steps
      v-if="state.streetToCall === 'river' && state.presetCommunityCards.turnSet && state.presetHoleCards.bothSet"
      v-bind:state="state"
      v-bind:textWhenFiveCardHandPercentagesArentUpToDate="getTextWhenFiveCardHandPercentagesArentUpToDate(3)"
      v-bind:authorized="authorized"
      v-on:setIndividualPresetCommunityCard="setIndividualPresetCommunityCard($event.card, $event.position)"
      v-on:removePresetCommunityCard="removePresetCommunityCard($event)"
      v-on:setPotOdds="setPotOdds($event)"
      v-on:setRealOddsLHS="setRealOddsLHS($event)"
      v-on:setBettingCriteria="setBettingCriteria()"
    />
    <section v-if="ableToRunEquityCalculations">
      <initial-conclusion
        v-bind:potOdds="state.potOdds"
        v-bind:equity="equity"
        v-bind:streetToCall="state.streetToCall"
        v-bind:breakevenPercentageViaPotOdds="$store.getters['guidedAnalysesCalling/breakevenPercentageViaPotOdds']"
      />
      <final-conclusion
        v-if="state.streetToCall !== 'river'"
        v-bind:potOdds="state.potOdds"
        v-bind:equity="equity"
        v-bind:realOddsLHS="state.realOddsLHS"
        v-bind:breakevenPercentageViaRealOdds="$store.getters['guidedAnalysesCalling/breakevenPercentageViaRealOdds']"
        v-on:setRealOddsLHS="setRealOddsLHS($event)"
      />
      <long-run-disclaimer/>
      <hr>
      <create-sharable-url-button
        v-bind:disabled="!authorized"
        v-on:createSharableUrl="$emit('createSharableUrl', {
          method: 'createSharableGuidedAnalysesCallingURL',
          state: state,
          approximateWaitTime: 5,
        })"
      />
      <save-scenario-button
        v-bind:savedScenario="savedScenario"
        v-bind:disabled="!authorized"
        v-on:saveScenario="$emit('saveScenario', {
          method: 'createSharableGuidedAnalysesCallingURL',
          savedScenarioPropertyName: 'shouldYouCall',
          state: state,
        })"
        v-on:updateScenario="$emit('updateScenario', {
          method: '_constructGuidedAnalysesCallingData',
          state: state,
        })"
      />
    </section>
    <onboarding-modal
      toolName="should you call tool"
      urlPath="should-you-call"
    />
    <help-icon path="/help/should-you-call" />
  </section>
</template>

<script>
import ReadonlyAlert from '@/components/utilities/readonly-alert';
import SavedScenarioAlert from '@/components/utilities/saved-scenario-alert';
import StreetSelector from '@/components/guided-analyses/street-selector';
import NumOpponentsSelector from '@/components/guided-analyses/num-opponents-selector';
import HoleCardsSelector from '@/components/controls/card-selection/hole-cards-selector';
import PreflopSteps from '@/components/guided-analyses/should-you-call/preflop-steps';
import FlopSteps from '@/components/guided-analyses/should-you-call/flop-steps';
import TurnSteps from '@/components/guided-analyses/should-you-call/turn-steps';
import RiverSteps from '@/components/guided-analyses/should-you-call/river-steps';
import InitialConclusion from '@/components/guided-analyses/should-you-call/conclusions/initial-conclusion';
import FinalConclusion from '@/components/guided-analyses/should-you-call/conclusions/final-conclusion';
import LongRunDisclaimer from '@/components/guided-analyses/should-you-call/conclusions/long-run-disclaimer';
import CreateSharableUrlButton from '@/components/controls/create-sharable-url-button';
import SaveScenarioButton from '@/components/controls/save-scenario-button';
import OnboardingModal from '@/components/onboarding-modal';
import HelpIcon from '@/components/help-icon';
import simulator from '@/services/simulator/simulator';
import _ from 'lodash';

export default {
  name: 'guided-analyses-should-you-call',
  components: {
    'readonly-alert': ReadonlyAlert,
    'saved-scenario-alert': SavedScenarioAlert,
    'street-selector': StreetSelector,
    'num-opponents-selector': NumOpponentsSelector,
    'hole-cards-selector': HoleCardsSelector,
    'preflop-steps': PreflopSteps,
    'flop-steps': FlopSteps,
    'turn-steps': TurnSteps,
    'river-steps': RiverSteps,
    'initial-conclusion': InitialConclusion,
    'final-conclusion': FinalConclusion,
    'long-run-disclaimer': LongRunDisclaimer,
    'create-sharable-url-button': CreateSharableUrlButton,
    'save-scenario-button': SaveScenarioButton,
    'onboarding-modal': OnboardingModal,
    'help-icon': HelpIcon,
  },
  computed: {
    state: function () {
      return this.$store.state.guidedAnalysesCalling;
    },
    savedScenario: function () {
      let routeId = this.$route.params.id;

      if (!this.authorized) {
        return false;
      }

      return this.$store.state.account.user.savedScenarios.shouldYouCall.find(function (savedScenario) {
        return savedScenario.url.includes(routeId);
      });
    },
    equity: function () {
      let equity = 100;

      this.state.players.forEach(function (player) {
        equity -= player.states[0].equity;
      });

      return equity.roundToTwoDecimalPlaces();
    },
    ableToRunFiveCardHandPercentageSimulationsForTheFlop: function () {
      if (!this.state.presetCommunityCards.flopSet) {
        return false;
      } else if (this.state.players.find(0).hasEmptyRange('preflop')) {
        return false;
      } else {
        return true;
      }
    },
    ableToRunFiveCardHandPercentageSimulationsForTheTurn: function () {
      if (!this.state.presetCommunityCards.turnSet) {
        return false;
      } else if (this.state.players.find(0).hasEmptyRange('flop')) {
        return false;
      } else {
        return true;
      }
    },
    ableToRunFiveCardHandPercentageSimulationsForTheRiver: function () {
      if (!this.state.presetCommunityCards.riverSet) {
        return false;
      } else if (this.state.players.find(0).hasEmptyRange('turn')) {
        return false;
      } else {
        return true;
      }
    },
    ableToRunEquityCalculations: function () {
      let ableToRunEquityCalculations = true;

      if (!this.state.presetHoleCards.bothSet) {
        ableToRunEquityCalculations = false;
      }

      else if (this.state.streetToCall === 'flop') {
        if (!this.state.presetCommunityCards.flopSet) {
          ableToRunEquityCalculations = false;
        }

        if (this.state.players.hasEmptyRange('flop')) {
          ableToRunEquityCalculations = false;
        }
      }

      else if (this.state.streetToCall === 'turn') {
        if (!this.state.presetCommunityCards.turnSet) {
          ableToRunEquityCalculations = false;
        }

        if (this.state.players.hasEmptyRange('turn')) {
          ableToRunEquityCalculations = false;
        }
      }

      else if (this.state.streetToCall === 'river') {
        if (!this.state.presetCommunityCards.riverSet) {
          ableToRunEquityCalculations = false;
        }

        if (this.state.players.hasEmptyRange('river')) {
          ableToRunEquityCalculations = false;
        }
      }

      return ableToRunEquityCalculations;
    },
    playerStateIndexToUseForSimulations: function () {
      if (this.state.streetToCall === 'flop') {
        return 1;
      } else if (this.state.streetToCall === 'turn') {
        return 2;
      } else if (this.state.streetToCall === 'river') {
        return 3;
      }
    },
    authorized: function () {
      return this.$store.getters['account/authorizedForBasic'];
    },
  },
  mounted: function () {
    let self = this;

    if (self.$route.params.id) {
      self.load();
    }

    else {
      self.$store.commit('guidedAnalysesCalling/setFiveCardHandValuesToDefaults', {
        settings: self.$store.state.account.user.settings,
        opponentTightness: 'straightforward',
      });
    }

    self.handleOnboard();

    // flop five card hand simulations
    self.$watch(function () {
      let sum = 0;

      self.state.players.forEach(function (player) {
        sum += player.states[0].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    }, _.debounce(self.runFiveCardHandPercentagesSimulationsForFlop, 1000));

    // turn five card hand simulations
    self.$watch(function () {
      let sum = 0;

      self.state.players.forEach(function (player) {
        sum += player.states[1].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    }, _.debounce(self.runFiveCardHandPercentagesSimulationsForTurn, 1000));

    // river five card hand simulations
    self.$watch(function () {
      let sum = 0;

      self.state.players.forEach(function (player) {
        sum += player.states[2].generalHands.numberOfSpecificHandsInRange;
      });

      return sum;
    }, _.debounce(self.runFiveCardHandPercentagesSimulationsForRiver, 1000));

    // equity
    self.$watch(
      function () {
        let sum = 0;

        self.state.players.forEach(function (player) {
          sum += player.states[0].generalHands.numberOfSpecificHandsInRange;
          sum += player.states[1].generalHands.numberOfSpecificHandsInRange;
          sum += player.states[2].generalHands.numberOfSpecificHandsInRange;
          sum += player.states[3].generalHands.numberOfSpecificHandsInRange;
        });

        return sum;
      },
      _.debounce(self.runEquityCalculations, 1000)
    );
  },
  methods: {
    // mounted
    load: function () {
      let self = this;

      self.$http.get(`${process.env.BASE_API_URL}/sharable-url-store/` + self.$route.params.id)
        .then(function (response) {
          if (!response || !response.data.storeState) {
            alert('Didn\'t get data back from server. Redirecting to the default guided analysis calling page.');
            self.$router.push('/guided-analyses/should-you-call');
          } else {
            self.$store.commit('guidedAnalysesCalling/setState', response.data.storeState);
            self.simulate();
          }
        })
        .catch(function () {
          alert('URL doesn\'t exist. Redirecting to the default guided analysis calling page.');
          self.$router.push('/guided-analyses/should-you-call');
        })
      ;
    },
    handleOnboard: function () {
      let self = this;

      if (
        !self.authorized ||
        !self.$store.state.account.user.settings.onboarding ||
        self.$store.state.account.user.settings.onboarding.shouldYouCall
      ) {
        return;
      }

      $(self.$el).find('.onboarding-modal').modal();
      self.$http
        .patch(`${process.env.BASE_API_URL}/user/onboard`, { page: 'shouldYouCall' })
        .then(function () {
          self.$store.commit('account/onboard', 'shouldYouCall');
        })
        .catch(function () {
          console.log('Error: Unable to onboard.');
        })
      ;
    },

    // other
    setStreetToCall: function (streetToCall) {
      this.$store.commit('guidedAnalysesCalling/setStreetToCall', streetToCall);

      if (streetToCall === 'flop') {
        this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'turn');
        this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'river');
        this.setRangesAccordingToFiltersForAllPlayers();
        this.simulate();
      } else if (streetToCall === 'turn') {
        this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'river');
        this.setRangesAccordingToFiltersForAllPlayers();
        this.simulate();
      } else if (streetToCall === 'river') {
        this.simulate();
      }
    },
    setPotOdds: function (potOdds) {
      this.$store.commit('guidedAnalysesCalling/setPotOdds', potOdds);
    },
    setRealOddsLHS: function (realOddsLHS) {
      this.$store.commit('guidedAnalysesCalling/setRealOddsLHS', realOddsLHS);
    },
    getTextWhenFiveCardHandPercentagesArentUpToDate: function (playerStateIndex) {
      if (
        (playerStateIndex === 1 && this.ableToRunFiveCardHandPercentageSimulationsForTheFlop) ||
        (playerStateIndex === 2 && this.ableToRunFiveCardHandPercentageSimulationsForTheTurn) ||
        (playerStateIndex === 3 && this.ableToRunFiveCardHandPercentageSimulationsForTheRiver)
      ) {
        return 'Running simulations...';
      } else {
        return 'Percentages of made hands, draws and combos are not up to date. Make sure that the range in the previous step has at least one hand selected so that simulations can be run to update the percentages.';
      }
    },

    // controls
    setPresetHoleCard: function (params) {
      this.$store.commit('guidedAnalysesCalling/removePresetHoleCard', params.position);
      this.$store.commit('guidedAnalysesCalling/setPresetHoleCard', {
        card: params.card,
        position: params.position
      });
      this.resetAllDoubleSliders();
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    removePresetHoleCard: function (position) {
      this.$store.commit('guidedAnalysesCalling/removePresetHoleCard', position);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    setIndividualPresetCommunityCard: function (card, position) {
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', position);
      this.$store.commit('guidedAnalysesCalling/setIndividualPresetCommunityCard', {
        card: card,
        position: position
      });
      this.resetAllDoubleSliders();
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    setBoard: function (boardCards) {
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'flop1');
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'flop2');
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'flop3');
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'turn');
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', 'river');
      this.$store.commit('guidedAnalysesCalling/setBoard', boardCards);
      this.resetAllDoubleSliders();
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    removePresetCommunityCard: function (position) {
      this.$store.commit('guidedAnalysesCalling/removePresetCommunityCard', position);
      this.setRangesAccordingToFiltersForAllPlayers();
      this.simulate();
    },
    addPlayer: function () {
      this.$store.commit('guidedAnalysesCalling/addPlayer', {
        settings: this.$store.state.account.user.settings,
        opponentTightness: 'straightforward',
      });
    },
    removePlayer: function () {
      this.$store.commit('guidedAnalysesCalling/removePlayer', this.state.players.numPlayers - 1);
      this.simulate();
    },

    setRangesAccordingToFiltersForAllPlayers: function () {
      this.state.players.forEach(function (player, i) {
        this.$store.commit('guidedAnalysesCalling/setRangesAccordingToFilters', i);
      }, this);
    },
    resetDoubleSlider: function (playerIndex) {
      if (this.$refs['player-' + playerIndex + '-0'] && this.$refs['player-' + playerIndex + '-0'][0]) {
        this.$refs['player-' + playerIndex + '-0'][0].resetDoubleSlider();
      }
    },
    resetAllDoubleSliders: function () {
      this.state.players.forEach(function (player, playerIndex) {
        if (this.state.presetCommunityCards.possiblePlayerStateIndexes.length > 1) {
          this.resetDoubleSlider(playerIndex, 0);
        }
      }, this);
    },

    simulate: function () {
      this
        .runEquityCalculations()
        .then(this.runFiveCardHandPercentagesSimulationsForFlop)
        .then(this.runFiveCardHandPercentagesSimulationsForTurn)
        .then(this.runFiveCardHandPercentagesSimulationsForRiver)
      ;
    },
    runEquityCalculations: function () {
      if (!this.ableToRunEquityCalculations) {
        return Promise.resolve();
      }

      let self = this;
      let weightedRanges = self._getWeightedRanges();
      let numberOfSimulations = self.$store.state.account.user.settings.equityCalculator.numberOfSimulations;

      return simulator
        .getEquities(weightedRanges, self.state.presetCommunityCards.array, numberOfSimulations, function () {})
        .then(function (simulationResults) {
          self.$store.commit('guidedAnalysesCalling/updateEquityResults', {
            simulationResults: simulationResults,
            numberOfSimulations: numberOfSimulations,
            rakePercentage: self.$store.state.account.user.settings.equityCalculator.rakeAndTip.rakePercentage,
            tipPercentage: self.$store.state.account.user.settings.equityCalculator.rakeAndTip.tipPercentage
          });
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    _getWeightedRanges: function () {
      let weightedRanges = [];
      let heroWeightedRange = [
        [this.state.presetHoleCards.holeCardOne, this.state.presetHoleCards.holeCardTwo]
      ];
      let playerStateIndexToUseForSimulations = this.playerStateIndexToUseForSimulations;

      weightedRanges.push(heroWeightedRange);
      this.state.players.forEach(function (player) {
        weightedRanges.push(player.states[playerStateIndexToUseForSimulations].generalHands.weightedRange);
      }, this);

      return weightedRanges;
    },
    runFiveCardHandPercentagesSimulationsForFlop: function () {
      let self = this;

      if (!self.ableToRunFiveCardHandPercentageSimulationsForTheFlop) {
        return Promise.resolve();
      }

      return simulator
        .getFlopFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('guidedAnalysesCalling/setLastSimulationInfo', 1);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    runFiveCardHandPercentagesSimulationsForTurn: function () {
      let self = this;

      if (!self.ableToRunFiveCardHandPercentageSimulationsForTheTurn) {
        return Promise.resolve();
      }

      return simulator
        .getTurnFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('guidedAnalysesCalling/setLastSimulationInfo', 2);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    runFiveCardHandPercentagesSimulationsForRiver: function () {
      let self = this;

      if (!self.ableToRunFiveCardHandPercentageSimulationsForTheRiver) {
        return Promise.resolve();
      }

      return simulator
        .getRiverFiveCardHandPercentages(self.state.players, self.state.presetCommunityCards)
        .then(function () {
          self.setRangesAccordingToFiltersForAllPlayers();
          self.$store.commit('guidedAnalysesCalling/setLastSimulationInfo', 3);
        })
        .catch(function (e) {
          console.error(e);
          alert('Encountered an unexpected problem while running the simulations.');
        })
      ;
    },
    setBettingCriteria: function () {
      if (
        (!this.state.presetHoleCards.bothSet) ||
        (this.state.players.numPlayers > 1) ||
        (this.state.streetToCall === 'flop' && !this.state.presetCommunityCards.flopSet) ||
        (this.state.streetToCall === 'turn' && !this.state.presetCommunityCards.turnSet) ||
        (this.state.streetToCall === 'river' && !this.state.presetCommunityCards.riverSet)
      ) {
        return;
      }

      this._initializeBettingCriteriaSimulationProgress();
      this.$store.dispatch('guidedAnalysesCalling/setBettingCriteria', {
        incrementSimulationProgress: this._incrementBettingCriteriaSimulationProgress,
        resetSimulationProgress: this._resetBettingCriteriaSimulationProgress,
        numberOfSimulations: this.$store.state.account.user.settings.equityCalculator.numberOfSimulations,
        rakePercentage: this.$store.state.account.user.settings.equityCalculator.rakeAndTip.rakePercentage,
        tipPercentage: this.$store.state.account.user.settings.equityCalculator.rakeAndTip.tipPercentage,
      });
    },
    _initializeBettingCriteriaSimulationProgress: function () {
      $(this.$el).find('.simulate-button-container .text').text('Simulating...');
      this._incrementBettingCriteriaSimulationProgress(0);
    },
    _incrementBettingCriteriaSimulationProgress: function (value) {
      let $simulationProgress = $(this.$el).find('.simulation-progress');
      let $simulationButton = $(this.$el).find('.simulate-button-container .simulate-button');
      let $simulationButtonProgressBox = $(this.$el).find('.simulate-button-container .progress-box');

      $simulationProgress.css('width', value + '%');
      $simulationButton.prop('disabled', true);
      $simulationButtonProgressBox.css('z-index', '1');
      $simulationButtonProgressBox.css('width', value + '%');
      $simulationButtonProgressBox.css('cursor', 'not-allowed');
    },
    _resetBettingCriteriaSimulationProgress: function () {
      let $simulationProgress = $(this.$el).find('.simulation-progress');
      let $simulationButton = $(this.$el).find('.simulate-button-container .simulate-button');
      let $simulationButtonText = $(this.$el).find('.simulate-button-container .text');
      let $simulationButtonProgressBox = $(this.$el).find('.betting-criteria-table .simulate-button-container .progress-box');

      $simulationProgress.css('width', '0%');
      $simulationButton.prop('disabled', false);
      $simulationButtonText.text('Simulate');
      $simulationButtonProgressBox.css('z-index', '-1');
      $simulationButtonProgressBox.css('cursor', 'auto');
    },
  },
};
</script>

<style lang="scss" scoped>
  iframe {
    max-width: 100%;
  }

  .create-sharable-url-button-container, .save-scenario-button-container {
    width: 100%;
  }

  .save-scenario-button-container {
    margin-top: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/guided-analyses/should-you-call/should-you-call.vue